/**
 * TREE SPECIES PROFILES AND LIFE STAGES — what a species IS, as parameters,
 * separately from how one is grown.
 *
 * The kit this replaces was eight hand-authored builds per kind, each a
 * function with its numbers written into it. That got real trees — `TREES.md`
 * is what it taught — and it has two limits nothing inside it can fix. Eight
 * variants is eight trees, so a wood is the same eight over and over. And a
 * small tree is a big tree scaled down, because scale is the only thing the
 * world varies per plant: **a sapling is a miniature adult**, which is the one
 * thing a tree is never allowed to be.
 *
 * So a species stops being a builder and becomes a *profile* — the range of
 * forms it is allowed to take — and an individual is drawn out of that range
 * by (seed, age). Two trees of one species at one age differ because the seed
 * differs; the same tree at two ages differs because growth is not scaling.
 *
 * ## What is a range and what is a curve
 *
 * Two kinds of number live here and they are not interchangeable:
 *
 *  · A **`Range`** is per-INDIVIDUAL. The seed picks a point in it, and the
 *    point holds for that tree's whole life. Trunk lean, limb count, crown
 *    asymmetry — the things that make this oak *this* oak.
 *  · An **`AgeCurve`** is per-STAGE. It says what the species does as it
 *    grows, and it is the same story for every individual of the species.
 *    Apical dominance falls; the crown widens; the bole thickens faster than
 *    it lengthens; deadwood arrives only at the end.
 *
 * Getting those the wrong way round is how a system like this produces "more
 * random trees" instead of trees that differ for reasons.
 *
 * ## Nothing here draws anything
 *
 * This module is data and the arithmetic for reading it. `grow.ts` is the only
 * thing that turns it into geometry, and it works from the resolved parameters
 * alone — so a new species is a new entry here, not a new builder.
 */

/** A per-individual range. The seed picks a point; `bias` shapes the draw. */
export interface Range {
  lo: number;
  hi: number;
  /**
   * How the draw is shaped, as an exponent on a uniform [0,1). 1 is uniform,
   * over 1 crowds toward `lo`, under 1 crowds toward `hi`. Most morphology is
   * not uniform — most oaks are ordinary oaks and a few are enormous — and a
   * uniform draw over a wide range makes every tree equally extreme, which
   * reads as noise rather than as a population.
   */
  bias?: number;
}

export const range = (lo: number, hi: number, bias = 1): Range => ({ lo, hi, bias });

/** Draw a point from a range with a unit random. */
export const pick = (r: Range, u: number): number =>
  r.lo + (r.hi - r.lo) * Math.pow(Math.min(0.999999, Math.max(0, u)), r.bias ?? 1);

/* ======================================================================
 * LIFE STAGES
 * ==================================================================== */

/**
 * The six stages. `age01` runs 0..1 across a tree's whole life and the stage
 * is read off it, so a caller may scrub continuously and still ask which
 * stage it is in.
 *
 * The boundaries are not even. A tree spends a small part of its life as a
 * sapling and a very long part mature — and the two ends are where the shape
 * changes fastest, so the stages are narrow there and wide in the middle,
 * which is what puts the visible change where the scrub is.
 */
export type StageName =
  'sapling' | 'juvenile' | 'young' | 'mature' | 'old' | 'ancient';

export const STAGES: ReadonlyArray<{ name: StageName; from: number }> = [
  { name: 'sapling', from: 0.00 },
  { name: 'juvenile', from: 0.10 },
  { name: 'young', from: 0.24 },
  { name: 'mature', from: 0.44 },
  { name: 'old', from: 0.76 },
  { name: 'ancient', from: 0.91 },
];

export function stageAt(age01: number): StageName {
  let s: StageName = 'sapling';
  for (const st of STAGES) if (age01 >= st.from) s = st.name;
  return s;
}

/** The centre of a stage, for a caller that wants one tree per stage. */
export function stageAge(name: StageName): number {
  const i = STAGES.findIndex((s) => s.name === name);
  const from = STAGES[i].from;
  const to = i + 1 < STAGES.length ? STAGES[i + 1].from : 1;
  return (from + to) / 2;
}

/**
 * AN AGE CURVE — four control points at sapling / young / mature / ancient,
 * read with a smooth interpolation across `age01`.
 *
 * Four rather than six because growth is not six things: a curve almost
 * always rises to a peak and falls, or rises and holds, and four points say
 * that without the author having to fill in the ones between. Six would be
 * six numbers to keep consistent per parameter per species, which is how a
 * table stops being read.
 */
export type AgeCurve = readonly [sapling: number, young: number, mature: number, ancient: number];

/** Read a curve at an age. Catmull-Rom-ish: smooth, and it hits its points. */
export function curveAt(c: AgeCurve, age01: number): number {
  const t = Math.min(1, Math.max(0, age01)) * 3;
  const i = Math.min(2, Math.floor(t));
  const f = t - i;
  const a = c[i], b = c[i + 1];
  // smoothstep between the bracketing points: growth has no corners in it
  return a + (b - a) * (f * f * (3 - 2 * f));
}

/* ======================================================================
 * THE PROFILE
 * ==================================================================== */

/**
 * How a species distributes its foliage. This is the single strongest
 * species signal there is — stronger than branch angle, stronger than crown
 * width — because it decides what the silhouette is MADE of.
 */
export type FoliageStyle =
  /** Rounded masses on the twigs that grew them. Oaks, most broadleafs. */
  | 'clumped'
  /** Flat overlapping rafts lying along the limb. A spreading oak's canopy. */
  | 'rafted'
  /** Pads in rings about the axis, level on level. A conifer's boughs. */
  | 'tiered'
  /** Small masses close to a vertical column. A poplar. */
  | 'columnar'
  /** Sprays that continue DOWNWARD off the branch that carries them. Willow. */
  | 'weeping';

export interface TreeSpeciesProfile {
  id: string;
  label: string;
  /** Which `VegKind` this species stands in for, where the world needs one. */
  kind: string;
  foliage: FoliageStyle;
  /**
   * How tall a MATURE one of these stands, in metres. The generator works in
   * a build space where a mature tree is exactly 1, so this is the only place
   * a species' real size is stated and the only number a caller multiplies by.
   *
   * These are real mature-tree metres, and the island's are nowhere near
   * them. Measured: a shipped broadleaf is 6.2 m and a spreading oak **3.3 m**,
   * against a reference figure drawn 3.98 m tall — so the island's oaks stand
   * below the shoulder of the people who live under them. `world/chunk.ts`'s
   * height law is where that is decided and it is not this module's to move;
   * what this module can do is state what a mature tree of the species
   * actually is, so the gap is visible the moment one is grown.
   */
  matureHeight: number;
  /**
   * HOW MUCH WIDER THE BUILD HAS TO BE THAN IT LOOKS — the world's own squeeze,
   * pre-compensated.
   *
   * `world/chunk.ts` gives every plant a `ratio` and `render/chunkView.ts`
   * composes `scale(scaleXZ, scaleY, scaleXZ)`, so a crown designed to look
   * right at uniform scale is stretched or squeezed in two axes before anybody
   * sees it. A conifer's ratio runs 0.15–0.41: the generated spire measured a
   * correct 0.36 w:h on its own and came out **0.35 of its far build's width**
   * in-world — a pencil, and a near variant that narrow pops visibly at the
   * LOD boundary (`TREES.md` §3, which measured the same failure at 0.64).
   *
   * `TREES.md` §2 states the rule this implements: *design the build for the
   * squeeze, not in spite of it.* Each number here is measured, not chosen —
   * it is `1 / (near width ÷ far width)` from `tools/trees.ts --near-far`,
   * iterated until every family sits near 1.0.
   */
  buildSqueeze: number;
  /**
   * The world's own `ratio` for this kind, at the middle of its span —
   * `world/chunk.ts`'s `ratio` column, which becomes `scaleXZ = h × ratio`.
   *
   * It exists so a LAB can draw what the WORLD draws. `buildSqueeze` above
   * pre-compensates for this squeeze, so a build that comes out a correct
   * spire in the world is enormously wide at uniform scale: the workshop was
   * showing a 45 m × 53 m conifer beside a 16 m × 5 m one in the game, and
   * every judgement made in front of it would have been about a tree that
   * does not exist. A lab that flatters is worse than no lab.
   */
  worldRatio: number;
  /**
   * WIND FLEX — metres of tip travel per metre of height, before the gust
   * curve, exactly as `FLEX` in `render/vegetation.ts` means it.
   *
   * It is a species property and it is SMALL. The generator's first cut passed
   * its role multipliers (0.1 for a bole, up to 1.05 for foliage) straight in
   * as flex, having read them as "relative stiffness" — but `part()` takes an
   * absolute coefficient, and the kit's own are 0.026 (conifer) to 0.10
   * (willow). Every generated tree therefore swayed about twenty times too
   * hard. The role multipliers are still what varies stiffness UP the tree;
   * this is what they are multiplied BY.
   */
  windFlex: number;

  /* ---- the individual ------------------------------------------------ */
  /**
   * VIGOUR — this tree's own size against the species' average, at any age.
   *
   * The first cut had none, and that was the single biggest reason two seeds
   * of one species felt like the same tree twice: `size` came only from the
   * age curve, so **every tree of a species at an age was the same nominal
   * height** and the seed could only move proportions. Measured over eight
   * seeds, the spread of mature heights was 5.3% — decoration.
   *
   * A real even-aged stand varies far more than that: some trees got the
   * light and some did not, and the difference is permanent. This is the
   * variable that makes a wood look like a wood.
   */
  vigour: Range;

  /* ---- the trunk ---------------------------------------------------- */
  /** Bole height as a share of the whole tree, at maturity. */
  trunkShare: Range;
  /**
   * Radius at the foot, in build units, at maturity — where the tree's whole
   * height is 1, so this is a FRACTION OF ITS HEIGHT and it is small.
   *
   * The first cut carried the old kit's numbers (0.10–0.15) into this space
   * and gave a mature oak a bole 0.23 wide on 0.61 of height: a stump with a
   * bush on it, 37% of the tree's height in trunk diameter. A real oak is
   * about 6% — a 1.2 m bole on a 20 m tree — and a conifer nearer 3%. The
   * root flare multiplies this again, and age multiplies it a third time
   * through the `girth` curve, so the mature figure is the smallest of the
   * three and the ancient one can be half again as thick.
   */
  trunkRadius: Range;
  /** How much the bole leans off plumb, radians. */
  lean: Range;
  /** How much the bole WANDERS as it rises — a separate thing from lean. */
  sinuosity: Range;
  /** Root flare, as a multiple of the trunk radius. */
  rootFlare: Range;

  /* ---- the branches -------------------------------------------------- */
  /** Primary limbs at maturity. */
  limbs: Range;
  /**
   * WHERE ALONG THE TRUNK the primaries leave, as [from, to] shares of the
   * tree's height.
   *
   * This is a species fact and it was briefly a bug: the oak's crown was
   * tiering like a pagoda because its limbs left over the bole's whole upper
   * half, and the fix — tighten the band so an oak forks at one level — was
   * applied to the shared grammar and therefore to everything. It made the
   * poplar a lollipop overnight: a tree whose foliage is supposed to run in a
   * sleeve down most of its height had every limb leaving from one point, so
   * it came out a ball on a stick.
   *
   * A narrow band is a tree that forks (oak). A wide one is a tree that
   * carries branches the whole way up (conifer, poplar). It is exactly the
   * kind of thing that must be a parameter rather than a special case — see
   * this file's header, and `grow.ts`'s rule about there being no `if
   * (species === …)` in it.
   */
  limbSpan: readonly [from: number, to: number];
  /** Orders of branching below the primaries at maturity (2 = secondaries + twigs). */
  depth: Range;
  /** Primary elevation off horizontal, radians. Low is a spreading tree. */
  limbRise: Range;
  /** How much a bough turns UP over its own length. Negative sags first. */
  limbCurve: Range;
  /** Primary length, in build units. */
  reach: Range;
  /** How much shorter each order is than its parent. */
  lengthDecay: Range;
  /** Children per branch, at each order below the primary. */
  branchOut: Range;

  /* ---- the crown ----------------------------------------------------- */
  /** How lopsided the crown is, 0 even to 1 all on one side. */
  asymmetry: Range;
  /** Foliage mass radius, build units, at maturity. */
  clumpSize: Range;
  /** Masses per twig. */
  clumpDensity: Range;
  /** How much flatter than round a mass is (1 is round). */
  clumpFlatten: Range;
  /** How far a mass stretches along its branch (1 is round). */
  clumpStretch: Range;

  /* ---- colour -------------------------------------------------------- */
  /** Per-individual multiplier on the instance's own foliage colour. */
  foliageTint: Range;
  /** Per-individual multiplier on bark value. */
  barkTint: Range;

  /* ---- what age does -------------------------------------------------
   * Every curve is a MULTIPLIER on the mature value unless it says otherwise.
   */
  age: {
    /** Overall size against the mature tree. The one curve that IS a scale. */
    size: AgeCurve;
    /** Bole share of height. A sapling is nearly all stem; an old tree is bole. */
    trunkShare: AgeCurve;
    /** Trunk radius against height — girth outruns height for a whole life. */
    girth: AgeCurve;
    /** How many primaries, against the mature count. */
    limbs: AgeCurve;
    /** How many orders of branching. Under 1 truncates the hierarchy. */
    depth: AgeCurve;
    /** Crown width against the mature crown. */
    crownWidth: AgeCurve;
    /**
     * APICAL DOMINANCE — how strongly the leader outranks its limbs. A sapling
     * is a stick with a bud on top; an old broadleaf has lost its leader
     * entirely and is a competition between equals. This is the parameter
     * that makes a young tree look young.
     */
    apical: AgeCurve;
    /** Foliage per branch, against mature. Old trees thin, ancients gap. */
    foliage: AgeCurve;
    /** Crown asymmetry multiplier. Age is not symmetric. */
    asymmetry: AgeCurve;
    /** Share of branch tips that are dead wood — bare, pale, no foliage. */
    deadwood: AgeCurve;
    /** Bark value: young bark is paler and smoother than old. */
    barkValue: AgeCurve;
  };
}

/* ======================================================================
 * THE SPECIES
 * ==================================================================== */

const R = range;

/**
 * ROUND BROADLEAF — the default tree. Trunk to a clear head, limbs that
 * curve up, rounded masses on the twigs that grew them, holes THROUGH the
 * crown (`TREES.md` §1).
 *
 * Its age story is the standard one and the others are told against it: the
 * leader is everything at first and gone by the end, the bole goes from a
 * tenth of the tree to nearly half, and girth keeps growing long after height
 * has stopped.
 */
const BROADLEAF: TreeSpeciesProfile = {
  id: 'broadleaf', label: 'round broadleaf', kind: 'broadleaf', worldRatio: 0.98, buildSqueeze: 1.57, windFlex: 0.038, matureHeight: 22, foliage: 'clumped',
  vigour: R(0.74, 1.30),
  trunkShare: R(0.30, 0.46), trunkRadius: R(0.019, 0.032, 1.5),
  lean: R(0.01, 0.14, 1.7), sinuosity: R(0.02, 0.13), rootFlare: R(1.15, 1.45),
  limbSpan: [0.46, 0.92],
  limbs: R(3, 6), depth: R(2, 3), limbRise: R(0.30, 0.58),
  limbCurve: R(0.16, 0.44), reach: R(0.28, 0.44), lengthDecay: R(0.42, 0.58),
  branchOut: R(2, 3.4),
  asymmetry: R(0, 0.5, 1.8), clumpSize: R(0.062, 0.082), clumpDensity: R(2.0, 3.3),
  clumpFlatten: R(0.74, 0.98), clumpStretch: R(1.0, 1.25),
  foliageTint: R(0.88, 1.10), barkTint: R(0.85, 1.12),
  age: {
    size:       [0.10, 0.42, 1.00, 0.98],
    trunkShare: [0.34, 0.72, 1.00, 1.12],
    girth:      [0.30, 0.62, 1.00, 1.34],
    limbs:      [0.22, 0.62, 1.00, 0.92],
    depth:      [0.28, 0.70, 1.00, 0.94],
    crownWidth: [0.11, 0.48, 1.00, 1.06],
    apical:     [1.00, 0.66, 0.24, 0.06],
    foliage:    [0.44, 0.86, 1.00, 0.62],
    asymmetry:  [0.35, 0.70, 1.00, 1.85],
    deadwood:   [0.00, 0.00, 0.04, 0.34],
    barkValue:  [1.16, 1.06, 1.00, 0.86],
  },
};

/**
 * SPREADING OAK — the wide one. Short heavy bole, limbs that SAG before they
 * lift, flat rafts of foliage lying along them, and the negative space UNDER
 * the canopy rather than through it. Its ancient form is the point of the
 * species: an oak that has stood a long time is mostly trunk and elbow.
 */
const OAK: TreeSpeciesProfile = {
  id: 'oak', label: 'spreading oak', kind: 'spreading', worldRatio: 1.72, buildSqueeze: 1.45, windFlex: 0.026, matureHeight: 20, foliage: 'rafted',
  vigour: R(0.72, 1.34),
  trunkShare: R(0.20, 0.32), trunkRadius: R(0.030, 0.048, 1.4),
  lean: R(0.00, 0.07), sinuosity: R(0.03, 0.16), rootFlare: R(1.30, 1.62),
  /*
   * `reach` was 0.52–0.70 and gave a mature oak 35 m of spread on 12 m of
   * height — 2.9:1, which is a sprawl rather than a spread. The number was
   * inherited from the island's own `ratio` column (1.20–2.25 on a plant the
   * world then stretches again), and that column is doing a different job:
   * making an oak woodland read as its own country from the air. A real
   * open-grown oak is nearer 1.3:1, and 1.5:1 is as wide as this profile goes.
   */
  limbSpan: [0.66, 0.90],
  limbs: R(3, 5), depth: R(2, 3), limbRise: R(0.14, 0.32),
  limbCurve: R(-0.44, -0.22), reach: R(0.30, 0.40), lengthDecay: R(0.30, 0.46),
  branchOut: R(2, 3),
  /*
   * BROADER, and there are more of them. An oak's canopy is not a set of
   * masses that happen to be stretched — it is flat rafts of leaves lying
   * along the limbs, wide enough to touch their neighbours. `clumpStretch`
   * runs a raft out along its branch and `clumpFlatten` presses it down, both
   * further than any other species here, and both before the world's own
   * ×1.20–2.25 ratio stretches the whole plant again.
   */
  asymmetry: R(0, 0.6, 1.6), clumpSize: R(0.026, 0.035), clumpDensity: R(1.2, 1.8),
  /*
   * Flatter than round, but not a plate. At 0.36–0.50 the rafts read as
   * mushroom caps stacked in tiers — a pagoda, not a canopy. A leaf raft has
   * depth; what makes it a raft is that it is wider than it is deep, not that
   * it is two-dimensional.
   */
  clumpFlatten: R(0.50, 0.66), clumpStretch: R(1.45, 1.85),
  foliageTint: R(0.86, 1.06), barkTint: R(0.78, 1.05),
  age: {
    size:       [0.09, 0.38, 1.00, 1.02],
    trunkShare: [0.42, 0.78, 1.00, 1.08],
    girth:      [0.24, 0.55, 1.00, 1.55],
    limbs:      [0.24, 0.60, 1.00, 0.86],
    depth:      [0.30, 0.68, 1.00, 0.88],
    crownWidth: [0.09, 0.42, 1.00, 1.14],
    // an oak loses its leader early and completely: by maturity the limbs
    // have won the argument and the crown goes OUT, with no point on top
    apical:     [1.00, 0.46, 0.04, 0.00],
    foliage:    [0.40, 0.84, 1.00, 0.54],
    asymmetry:  [0.30, 0.66, 1.00, 2.05],
    deadwood:   [0.00, 0.00, 0.06, 0.46],
    barkValue:  [1.18, 1.05, 1.00, 0.80],
  },
};

/**
 * CONIFER — a leader that never gives up. The one species whose apical
 * dominance stays high its whole life, which is why it is a spire and not a
 * crown; what age changes is the SKIRT, as the lower boughs are shaded out
 * and die back, so an old conifer is a bare pole under a narrow top.
 */
const CONIFER: TreeSpeciesProfile = {
  id: 'conifer', label: 'conifer', kind: 'conifer', worldRatio: 0.28, buildSqueeze: 7.30, windFlex: 0.020, matureHeight: 34, foliage: 'tiered',
  vigour: R(0.78, 1.26),
  trunkShare: R(0.82, 0.95), trunkRadius: R(0.012, 0.022, 1.5),
  lean: R(0.00, 0.06), sinuosity: R(0.01, 0.06), rootFlare: R(1.10, 1.30),
  /*
   * `reach` was 0.16–0.30 and gave a mature conifer 21 m of crown on 40 m of
   * height — 0.54 w:h, which is not a conifer, it is a bush. Measured beside
   * the willow it was the SAME proportion (0.60), and two families that
   * should be unmistakable were reading alike.
   *
   * The `tiered` style takes its ring radius from how far the branches
   * actually reach, so this is the only number that sets the spire. A spruce
   * runs 0.25–0.35 w:h; 0.09–0.16 here lands it near 0.30.
   */
  limbSpan: [0.14, 0.96],
  limbs: R(7, 11), depth: R(1, 2), limbRise: R(-0.16, 0.16),
  limbCurve: R(-0.20, 0.04), reach: R(0.09, 0.16), lengthDecay: R(0.40, 0.55),
  branchOut: R(2, 3),
  asymmetry: R(0, 0.28, 2.0), clumpSize: R(0.040, 0.058), clumpDensity: R(1.6, 2.6),
  clumpFlatten: R(0.34, 0.50), clumpStretch: R(1.25, 1.65),
  foliageTint: R(0.72, 0.94), barkTint: R(0.80, 1.02),
  age: {
    size:       [0.10, 0.44, 1.00, 1.00],
    trunkShare: [0.90, 0.96, 1.00, 1.00],
    girth:      [0.26, 0.58, 1.00, 1.42],
    limbs:      [0.30, 0.68, 1.00, 0.74],
    depth:      [0.40, 0.80, 1.00, 0.90],
    // a conifer sapling is a SPIKE — the one seedling in the kit whose
    // silhouette is already the adult's argument, only thinner
    crownWidth: [0.12, 0.48, 1.00, 0.80],
    // the one species that keeps its leader — that IS the conifer
    apical:     [1.00, 0.96, 0.88, 0.74],
    foliage:    [0.46, 0.88, 1.00, 0.64],
    asymmetry:  [0.40, 0.70, 1.00, 1.70],
    deadwood:   [0.00, 0.02, 0.10, 0.42],
    barkValue:  [1.14, 1.05, 1.00, 0.84],
  },
};

/**
 * RIVER POPLAR — tall, narrow, upright, and it often rises in several strong
 * leaders rather than one. Light canopy held close to the axis; the whole
 * silhouette is verticality, so nothing here is allowed to spread.
 */
const POPLAR: TreeSpeciesProfile = {
  id: 'poplar', label: 'river poplar', kind: 'columnar', worldRatio: 0.195, buildSqueeze: 2.37, windFlex: 0.028, matureHeight: 28, foliage: 'columnar',
  vigour: R(0.80, 1.24),
  /*
   * A poplar's bole is SHORT relative to its crown, and getting that wrong is
   * what made it a lollipop: at 0.55–0.76 the branched column was confined to
   * the top quarter of the tree and read as a ball on a stick — the exact
   * silhouette the brief names as a thing to avoid. A riparian poplar carries
   * foliage down most of its height in a narrow sleeve; the bole is the bit
   * below that, and it is a third of the tree, not three quarters.
   */
  trunkShare: R(0.30, 0.44), trunkRadius: R(0.011, 0.019, 1.6),
  lean: R(0.00, 0.05), sinuosity: R(0.02, 0.10), rootFlare: R(1.08, 1.26),
  limbSpan: [0.18, 0.94],
  limbs: R(5, 9), depth: R(1, 2), limbRise: R(0.85, 1.24),
  limbCurve: R(0.10, 0.30), reach: R(0.16, 0.26), lengthDecay: R(0.44, 0.60),
  branchOut: R(2, 3),
  asymmetry: R(0, 0.22, 2.2), clumpSize: R(0.048, 0.070), clumpDensity: R(1.25, 1.95),
  clumpFlatten: R(0.52, 0.70), clumpStretch: R(0.88, 1.10),
  foliageTint: R(0.94, 1.16), barkTint: R(1.05, 1.30),
  age: {
    size:       [0.12, 0.48, 1.00, 0.96],
    trunkShare: [0.72, 0.90, 1.00, 1.02],
    girth:      [0.30, 0.60, 1.00, 1.28],
    limbs:      [0.26, 0.66, 1.00, 0.88],
    depth:      [0.36, 0.76, 1.00, 0.92],
    crownWidth: [0.28, 0.64, 1.00, 1.02],
    apical:     [1.00, 0.84, 0.62, 0.40],
    foliage:    [0.48, 0.90, 1.00, 0.66],
    asymmetry:  [0.42, 0.72, 1.00, 1.60],
    deadwood:   [0.00, 0.00, 0.05, 0.38],
    barkValue:  [1.10, 1.04, 1.00, 0.90],
  },
};

/**
 * WILLOW — a scaffold first, and the hanging growth is real branch
 * architecture rather than a curtain hung off a pad (`TREES.md` §1). Its
 * primaries rise and its outer orders turn over and continue DOWNWARD, which
 * is what `weeping` means here. The open air is UNDER the canopy.
 */
const WILLOW: TreeSpeciesProfile = {
  id: 'willow', label: 'willow', kind: 'willow', worldRatio: 1.25, buildSqueeze: 0.49, windFlex: 0.072, matureHeight: 14, foliage: 'weeping',
  vigour: R(0.76, 1.28),
  trunkShare: R(0.24, 0.40), trunkRadius: R(0.026, 0.044, 1.4),
  lean: R(0.02, 0.16, 1.5), sinuosity: R(0.06, 0.22), rootFlare: R(1.35, 1.70),
  limbSpan: [0.42, 0.92],
  limbs: R(2, 4), depth: R(2, 3), limbRise: R(0.62, 1.05),
  limbCurve: R(0.10, 0.34), reach: R(0.38, 0.54), lengthDecay: R(0.48, 0.66),
  branchOut: R(2.4, 3.6),
  asymmetry: R(0, 0.42, 1.7), clumpSize: R(0.038, 0.058), clumpDensity: R(1.2, 1.75),
  clumpFlatten: R(0.60, 0.86), clumpStretch: R(0.85, 1.05),
  foliageTint: R(0.90, 1.12), barkTint: R(0.82, 1.06),
  age: {
    size:       [0.11, 0.44, 1.00, 0.94],
    trunkShare: [0.38, 0.76, 1.00, 1.10],
    girth:      [0.28, 0.58, 1.00, 1.48],
    limbs:      [0.30, 0.66, 1.00, 0.90],
    depth:      [0.32, 0.72, 1.00, 0.96],
    crownWidth: [0.12, 0.46, 1.00, 1.10],
    apical:     [1.00, 0.60, 0.20, 0.06],
    foliage:    [0.42, 0.86, 1.00, 0.58],
    asymmetry:  [0.36, 0.68, 1.00, 1.90],
    deadwood:   [0.00, 0.00, 0.05, 0.40],
    barkValue:  [1.12, 1.04, 1.00, 0.84],
  },
};

export const TREE_SPECIES: Readonly<Record<string, TreeSpeciesProfile>> = {
  broadleaf: BROADLEAF, oak: OAK, conifer: CONIFER, poplar: POPLAR, willow: WILLOW,
};

export const SPECIES_IDS: readonly string[] =
  ['broadleaf', 'oak', 'conifer', 'poplar', 'willow'];

/* ======================================================================
 * ENVIRONMENT — the seams, not the simulation.
 * ==================================================================== */

/**
 * What the world does to a tree that grew in it. Every field is neutral at
 * its default, so a caller that knows nothing about the site gets the
 * species' own form and pays nothing.
 *
 * Deliberately thin. `sim/` has no per-tree environmental history to give
 * this yet, and inventing one here would be a second world model to keep in
 * step with the real one. What this IS is the seam: when `world/chunk.ts` can
 * say how exposed a site is or how crowded, it assigns these and nothing else
 * has to change.
 */
export interface TreeEnvironment {
  /** −1 sheltered, 0 ordinary, +1 wind-hammered. Leans and thins the crown. */
  exposure?: number;
  /** −1 open ground, 0 ordinary, +1 crowded. Straighter, higher, narrower. */
  crowding?: number;
  /** −1 drought, 0 ordinary, +1 riparian. Fuller and looser. */
  water?: number;
  /** The bearing wind comes from, radians. Only read when `exposure` > 0. */
  windFrom?: number;
}

export const NEUTRAL_ENVIRONMENT: Readonly<Required<TreeEnvironment>> =
  Object.freeze({ exposure: 0, crowding: 0, water: 0, windFrom: 0 });
